"use client";

import { Radio as RadioPrimitive } from "@base-ui/react/radio";
import { RadioGroup as RadioGroupPrimitive } from "@base-ui/react/radio-group";
import { cva } from "../../lib/cva";
import type React from "react";
import { cn } from "../../lib/utils";
import { Icon } from "./icon";

export function RadioGroup({
  className,
  ...props
}: RadioGroupPrimitive.Props): React.ReactElement {
  return (
    <RadioGroupPrimitive
      className={cn("flex flex-col gap-3", className)}
      data-slot="radio-group"
      {...props}
    />
  );
}

// Unchecked is always `--background` in both modes, matching Checkbox — the
// dot reads as a hole cut into the surface hosting it, never a filled well.
export const radioVariants = cva(
  "relative inline-flex size-4.5 shrink-0 items-center justify-center rounded-full border border-[color-mix(in_oklab,var(--input),var(--border-strong)_50%)] bg-background not-dark:bg-clip-padding shadow-xs/5 outline-none transition-[box-shadow,border-color] not-data-disabled:not-data-checked:not-aria-invalid:not-focus-visible:hover:border-border-strong before:pointer-events-none before:absolute before:inset-0 before:rounded-full not-data-disabled:not-data-checked:not-aria-invalid:before:shadow-[0_1px_--theme(--color-black/4%)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background aria-invalid:border-destructive/36 focus-visible:aria-invalid:border-destructive/64 focus-visible:aria-invalid:ring-destructive/48 data-disabled:cursor-not-allowed data-disabled:opacity-64 sm:size-4 dark:focus-visible:aria-invalid:ring-destructive/24 dark:not-data-disabled:not-data-checked:not-aria-invalid:before:shadow-[0_-1px_--theme(--color-white/6%)] [[data-disabled],[data-checked],[aria-invalid]]:shadow-none",
);

export function Radio({
  className,
  ...props
}: RadioPrimitive.Root.Props): React.ReactElement {
  return (
    <RadioPrimitive.Root
      className={cn(radioVariants(), className)}
      data-slot="radio"
      {...props}
    >
      <RadioPrimitive.Indicator
        className="absolute -inset-px flex size-4.5 items-center justify-center rounded-full before:size-2 before:rounded-full before:bg-primary-foreground data-unchecked:hidden data-checked:bg-primary sm:size-4 sm:before:size-1.5"
        data-slot="radio-indicator"
      />
    </RadioPrimitive.Root>
  );
}

/**
 * A single choice among a few named options, as a bordered column of rows —
 * the list a phone's Repeat picker is: every option readable at any width,
 * one press, the check on the answer. Rows are {@link RadioListItem}s; the
 * group is a real radio group (arrow keys move the check, one tab stop).
 *
 * Use it where the options are few (four to eight), each fits one line, and
 * seeing them all at once is the point — a Select hides them, a row of pills
 * wraps unevenly, and plain radios spend a column on dots the rows do not
 * need. The list paints the `background` fill, one rung below the popover
 * or card it sits on, so it reads as a well the choice is made in.
 */
export function RadioList({
  className,
  ...props
}: RadioGroupPrimitive.Props): React.ReactElement {
  return (
    <RadioGroupPrimitive
      className={cn(
        "flex w-full flex-col overflow-clip rounded-lg border bg-background not-dark:bg-clip-padding",
        className,
      )}
      data-slot="radio-list"
      {...props}
    />
  );
}

export type RadioListItemProps = RadioPrimitive.Root.Props & {
  /** The option, one line. */
  label: React.ReactNode;
  /** What picking it comes to — a date, a count, a price — on the trailing
   *  edge in the caption rung, so the reader compares consequences, not
   *  names. Full ink on the checked row. */
  meta?: React.ReactNode;
};

/** One row of a {@link RadioList}: label, optional meta, and the check that
 *  marks the chosen row. 36px, hairline between rows, wash on hover and on
 *  the checked row. The hairline rides each row's TOP edge (dropped on the
 *  first): Base UI seats a hidden input after every radio, so no row is ever
 *  `:last-child` and a bottom hairline would double the list's frame. */
export function RadioListItem({
  label,
  meta,
  className,
  ...props
}: RadioListItemProps): React.ReactElement {
  return (
    <RadioPrimitive.Root
      className={cn(
        "flex min-h-9 w-full cursor-default select-none items-center gap-3 border-t px-3 text-left text-sm text-foreground outline-none first:border-t-0 hover:bg-wash-hover focus-visible:bg-wash-hover data-checked:bg-wash-active data-checked:font-medium data-disabled:cursor-not-allowed data-disabled:opacity-64",
        className,
      )}
      data-slot="radio-list-item"
      {...props}
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {meta != null ? (
        <span className="shrink-0 text-muted-foreground text-xs in-[[data-checked]]:text-foreground">
          {meta}
        </span>
      ) : null}
      <RadioPrimitive.Indicator
        className="flex w-4 shrink-0 justify-center data-unchecked:invisible"
        data-slot="radio-list-check"
        keepMounted
      >
        <Icon className="size-4" name="check" />
      </RadioPrimitive.Indicator>
    </RadioPrimitive.Root>
  );
}

export {
  RadioGroupPrimitive,
  RadioPrimitive,
};
